// src/components/ScenarioSliders.js
import React, { useMemo, useState } from "react";

/**
 * Props:
 *  - baselineMonthlyBurn (number | NaN)  // dollars per month (positive = burning, negative/0 = net generator)
 *  - baselineRunwayMonths (number | Infinity | NaN)
 *  - cashBalance (number | NaN)          // dollars
 *  - formatter (fn) => string             // format money nicely
 */
export default function ScenarioSliders({
  baselineMonthlyBurn,
  baselineRunwayMonths,
  cashBalance,
  formatter = (n) => `$${Number(n).toLocaleString()}`
}) {
  // Sliders: ±30%
  const [revPct, setRevPct] = useState(0);   // +% revenue change → reduces burn
  const [opexPct, setOpexPct] = useState(0); // +% opex change → increases burn

  const { ok, msg } = useMemo(() => {
    if (!isFinite(cashBalance) || isNaN(cashBalance)) {
      // If baseline runway is finite and burn is finite, we inferred cash in the page; otherwise can’t compute
      if (!isFinite(baselineRunwayMonths) || isNaN(baselineRunwayMonths)) {
        return { ok: false, msg: "Need either current Cash balance or a finite Runway × Burn to simulate." };
      }
    }
    if (isNaN(baselineMonthlyBurn)) {
      return { ok: false, msg: "Missing Monthly Burn. Load a dataset with operating cash/burn signals." };
    }
    return { ok: true, msg: null };
  }, [cashBalance, baselineRunwayMonths, baselineMonthlyBurn]);

  // Derived values
  const baseBurn = baselineMonthlyBurn; // dollars per month (positive means burning)
  const baseRunway = baselineRunwayMonths; // months
  const cash = isFinite(cashBalance) ? cashBalance : (isFinite(baseRunway) ? baseRunway * baseBurn : NaN);

  const result = useMemo(() => {
    if (!ok) return null;

    // Linear heuristics to mirror backend Phase-1 sensitivity:
    // Rev +10% → burn * 0.9; OpEx −10% → burn * 0.9
    // Therefore: newBurn = baseBurn * (1 - revPct/100 + opexPct/100)
    const factor = 1 - (revPct/100) + (opexPct/100);
    const newBurn = baseBurn * factor;

    let newRunway;
    if (newBurn <= 0) newRunway = Infinity;
    else if (isFinite(cash)) newRunway = cash / newBurn;
    else newRunway = NaN;

    return {
      newBurn,
      newRunway,
      deltaBurn: newBurn - baseBurn,
      deltaRunway: (isFinite(newRunway) && isFinite(baseRunway)) ? (newRunway - baseRunway) : NaN
    };
  }, [ok, baseBurn, baseRunway, cash, revPct, opexPct]);

  if (!ok) {
    return <div className="note-muted">{msg}</div>;
  }

  const burnText = (v) => {
    if (!isFinite(v)) return "∞";
    return formatter(v);
  };
  const rwText = (m) => (!isFinite(m) ? "∞" : `${m.toFixed(1)} mo`);
  const sign = (x) => (x > 0 ? "+" : "");

  return (
    <div className="scenario-wrap">
      <div className="slider-row">
        <label htmlFor="rev">Revenue change</label>
        <input
          id="rev" type="range" min={-30} max={30} step={1}
          value={revPct}
          onChange={(e) => setRevPct(Number(e.target.value))}
          aria-label="Revenue change percent"
        />
        <div className="pct">{revPct}%</div>
      </div>

      <div className="slider-row">
        <label htmlFor="opex">OpEx change</label>
        <input
          id="opex" type="range" min={-30} max={30} step={1}
          value={opexPct}
          onChange={(e) => setOpexPct(Number(e.target.value))}
          aria-label="Operating expenses change percent"
        />
        <div className="pct">{opexPct}%</div>
      </div>

      <div className="scenario-results">
        <div className="scenario-chip">
          <div className="label">Adjusted Monthly Burn</div>
          <div className="num">
            {result ? burnText(result.newBurn) : "—"}
          </div>
          <div className="note-muted">
            {result && isFinite(result.deltaBurn) ? `${sign(result.deltaBurn)}${formatter(Math.abs(result.deltaBurn))} vs baseline` : "—"}
          </div>
        </div>

        <div className="scenario-chip">
          <div className="label">Adjusted Runway</div>
          <div className="num">
            {result ? rwText(result.newRunway) : "—"}
          </div>
          <div className="note-muted">
            {result && isFinite(result.deltaRunway) ? `${sign(result.deltaRunway)}${Math.abs(result.deltaRunway).toFixed(1)} mo vs baseline` : (result?.newRunway === Infinity ? "Net generator (no burn)" : "—")}
          </div>
        </div>
      </div>
    </div>
  );
}
